import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import MonitorCard from "../components/MonitorCard";
import GroupCard from "../components/GroupCard";
import ConfirmModal from "../components/ConfirmModal";
import {
  listMonitors, createMonitor, deleteMonitor, CreateMonitorParams,
  listMonitorGroups, createMonitorGroup, deleteMonitorGroup, CreateMonitorGroupParams,
  setMonitorGroup,
} from "../lib/api";
import { Monitor, MonitorGroup, ALL_REGIONS } from "../types";
import { usePlan } from "../App";
import { Bone } from "../components/Skeleton";

const MONITOR_LIMIT: Record<string, number> = { free: 3, pro: 20 };

const FREE_INTERVALS = [
  { value: 10, label: "Every 10 min" },
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Every 1 hour" },
];

const PRO_INTERVALS = [
  { value: 1,  label: "Every 1 min" },
  { value: 5,  label: "Every 5 min" },
  ...FREE_INTERVALS,
];

const GROUP_COLORS = [
  "#FF2566", "#FF5C5C", "#FF4081", "#E91E63", "#D32F2F", "#C62828",
  "#FF9800", "#FF7043", "#FF6D00", "#E64A19", "#BF360C", "#FF8F00",
  "#FDDD48", "#FFC107", "#FFB300", "#C6A700", "#8BC34A", "#558B2F",
  "#39D98A", "#00BFA5", "#26A69A", "#00897B", "#43A047", "#2E7D32",
  "#2196F3", "#1976D2", "#0D47A1", "#29B6F6", "#0288D1", "#01579B",
  "#6366F1", "#7B1FA2", "#673AB7", "#512DA8", "#9C27B0", "#AB47BC",
];

type ViewMode = "individual" | "group";

export default function Monitors() {
  const qc = useQueryClient();
  const plan = usePlan();
  const isPro = plan === "pro";

  const [showForm, setShowForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("individual");

  const { data: monitorsData, isLoading: monitorsLoading, isError: monitorsError } = useQuery({
    queryKey: ["monitors"],
    queryFn: listMonitors,
  });

  const { data: groupsData, isLoading: groupsLoading, isError: groupsError } = useQuery({
    queryKey: ["monitor-groups"],
    queryFn: listMonitorGroups,
    enabled: isPro,
  });

  const deleteMut = useMutation({
    mutationFn: deleteMonitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitors"] }),
  });

  const deleteGroupMut = useMutation({
    mutationFn: deleteMonitorGroup,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monitor-groups"] });
      qc.invalidateQueries({ queryKey: ["monitors"] });
    },
  });

  const monitors: Monitor[] = monitorsData?.monitors ?? [];
  const groups: MonitorGroup[] = groupsData?.groups ?? [];
  const limit = MONITOR_LIMIT[plan] ?? 3;
  const atLimit = monitors.length >= limit;

  const groupMap = new Map(groups.map(g => [g.id, g]));

  const isLoading = monitorsLoading || (isPro && groupsLoading);

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700, color: "#EFF2F5" }}>Monitors</h1>
            <p style={{ margin: 0, fontSize: 13, color: "#98A1AB" }}>
              {monitors.length} of {limit} monitors used
              {plan === "free" && (
                <> · <Link to="/upgrade" style={{ color: "#FF2566", textDecoration: "none" }}>Upgrade for more</Link></>
              )}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isPro && (
              <>
                <div style={{ display: "flex", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                  <button
                    onClick={() => setViewMode("individual")}
                    style={{
                      padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                      background: viewMode === "individual" ? "rgba(255,255,255,0.1)" : "transparent",
                      color: viewMode === "individual" ? "#EFF2F5" : "#98A1AB",
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setViewMode("group")}
                    style={{
                      padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                      borderLeft: "1px solid rgba(255,255,255,0.12)",
                      background: viewMode === "group" ? "rgba(255,255,255,0.1)" : "transparent",
                      color: viewMode === "group" ? "#EFF2F5" : "#98A1AB",
                    }}
                  >
                    Groups
                  </button>
                </div>
                <button
                  onClick={() => { setShowGroupForm((v) => !v); setShowForm(false); }}
                  style={{
                    padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                    background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                    color: "#EFF2F5", borderRadius: 3,
                  }}
                >
                  {showGroupForm ? "Cancel" : "+ New Group"}
                </button>
              </>
            )}
            <button
              onClick={() => { setShowForm((v) => !v); setShowGroupForm(false); }}
              className="btn-primary"
              style={{ padding: "8px 16px", fontSize: 13, opacity: atLimit && !showForm ? 0.5 : 1 }}
              disabled={atLimit && !showForm}
              title={atLimit ? `Monitor limit reached (${limit})` : undefined}
            >
              {showForm ? "Cancel" : "+ Add Monitor"}
            </button>
          </div>
        </div>

        {plan === "free" && (
          <div style={{
            marginBottom: 20, padding: "14px 18px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 3, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
          }}>
            <p style={{ margin: 0, fontSize: 13, color: "#98A1AB", lineHeight: 1.5 }}>
              Free monitors check every 10 minutes from all regions. <strong style={{ color: "#EFF2F5" }}>Upgrade to Pro</strong> for 1-minute intervals, alerts, latency charts, uptime history, and monitor groups.
            </p>
            <Link to="/upgrade" className="btn-primary" style={{ padding: "8px 18px", fontSize: 13, whiteSpace: "nowrap", textDecoration: "none", flexShrink: 0 }}>
              Upgrade
            </Link>
          </div>
        )}

        {showGroupForm && (
          <GroupForm
            monitors={monitors}
            groups={groups}
            onSuccess={() => {
              setShowGroupForm(false);
              qc.invalidateQueries({ queryKey: ["monitor-groups"] });
              qc.invalidateQueries({ queryKey: ["monitors"] });
            }}
          />
        )}

        {showForm && (
          <MonitorForm
            plan={plan}
            groups={groups}
            onSuccess={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ["monitors"] }); }}
          />
        )}

        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[140, 190, 160].map((nameW, i) => (
              <div key={i} className="glass" style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    <Bone w={nameW} h={15} />
                    <Bone w={nameW + 60} h={11} />
                  </div>
                  <Bone w={8} h={8} br={50} />
                </div>
                <div style={{ display: "flex", gap: 16 }}>
                  <Bone w={56} h={12} />
                  <Bone w={32} h={12} />
                </div>
              </div>
            ))}
          </div>
        ) : (monitorsError || (isPro && groupsError)) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 16, color: "#EFF2F5" }}>Service temporarily unavailable</p>
            <p style={{ fontSize: 13 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          </div>
        ) : viewMode === "group" && isPro ? (
          <GroupView
            groups={groups}
            monitors={monitors}
            onDeleteGroup={(g) => {
              if (confirm(`Delete group "${g.name}"? Monitors in this group will not be deleted.`)) {
                deleteGroupMut.mutate(g.id);
              }
            }}
            onDeleteMonitor={(m) => {
              if (confirm(`Delete monitor "${m.name}"?`)) deleteMut.mutate(m.id);
            }}
          />
        ) : monitors.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 16 }}>No monitors yet.</p>
            <p style={{ fontSize: 13 }}>Add your first monitor to start tracking uptime and latency.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {monitors.map((m: Monitor) => (
              <MonitorCard
                key={m.id}
                monitor={m}
                group={m.group_id ? groupMap.get(m.group_id) ?? null : null}
                onDelete={() => {
                  if (confirm(`Delete monitor "${m.name}"?`)) {
                    deleteMut.mutate(m.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GroupView({
  groups,
  monitors,
  onDeleteGroup,
  onDeleteMonitor,
}: {
  groups: MonitorGroup[];
  monitors: Monitor[];
  onDeleteGroup: (g: MonitorGroup) => void;
  onDeleteMonitor: (m: Monitor) => void;
}) {
  const ungrouped = monitors.filter(m => !m.group_id);

  if (groups.length === 0 && monitors.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
        <p style={{ fontSize: 16 }}>No groups yet.</p>
        <p style={{ fontSize: 13 }}>Create a group to organize your monitors.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {groups.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {groups.map(g => (
            <GroupCard
              key={g.id}
              group={g}
              monitorCount={monitors.filter(m => m.group_id === g.id).length}
              onDelete={() => onDeleteGroup(g)}
            />
          ))}
        </div>
      )}

      {ungrouped.length > 0 && (
        <div>
          <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 600, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Ungrouped
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ungrouped.map(m => (
              <MonitorCard
                key={m.id}
                monitor={m}
                onDelete={() => onDeleteMonitor(m)}
              />
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#98A1AB" }}>
          <p style={{ fontSize: 14 }}>No groups yet. Click "+ New Group" to create one.</p>
        </div>
      )}
    </div>
  );
}

interface TCPPreset {
  label: string;
  port: number;
  description: string;
  send?: string;
  expect?: string;
}

const TCP_PRESETS: TCPPreset[] = [
  { label: "Redis", port: 6379, description: "In-memory cache / message broker", send: "PING\r\n", expect: "+PONG" },
  { label: "PostgreSQL", port: 5432, description: "Relational database" },
  { label: "MySQL", port: 3306, description: "Relational database" },
  { label: "SSH", port: 22, description: "Secure shell", expect: "SSH" },
  { label: "SMTP", port: 25, description: "Email server", expect: "220" },
  { label: "HTTPS", port: 443, description: "HTTPS / TLS endpoint" },
];

interface MonitorFormValues {
  name: string;
  url: string;
  tcp_host_port: string;
  method: string;
  interval_minutes: number;
  group_id: string;
  alert_latency_ms: string;
  alert_cert_expiry_days: string;
  alert_email: string;
  alert_webhook_url: string;
  tcp_tls: boolean;
  tcp_skip_verify: boolean;
  tcp_send_string: string;
  tcp_expect_string: string;
  tcp_timeout_secs: number;
}

function MonitorForm({ plan, groups, onSuccess, defaultGroupId }: { plan: string; groups?: MonitorGroup[]; onSuccess: () => void; defaultGroupId?: string }) {
  const isPro = plan === "pro";
  const defaultInterval = isPro ? 5 : 10;
  const intervals = isPro ? PRO_INTERVALS : FREE_INTERVALS;

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<MonitorFormValues>({
    defaultValues: {
      method: "GET",
      interval_minutes: defaultInterval,
      group_id: defaultGroupId ?? "",
      tcp_tls: false,
      tcp_skip_verify: false,
      tcp_timeout_secs: 5,
    },
  });
  const [apiError, setApiError] = useState<string | null>(null);

  const method = watch("method");
  const tcpTls = watch("tcp_tls");
  const isTCP = method === "TCP";

  const mut = useMutation({
    mutationFn: (data: CreateMonitorParams) => createMonitor(data),
    onSuccess,
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to create monitor"),
  });

  function applyPreset(preset: TCPPreset) {
    const current = (watch("tcp_host_port") || "").split(":")[0];
    setValue("tcp_host_port", current ? `${current}:${preset.port}` : `example.com:${preset.port}`);
    if (preset.send !== undefined) setValue("tcp_send_string", preset.send);
    if (preset.expect !== undefined) setValue("tcp_expect_string", preset.expect);
    if (preset.label === "HTTPS") setValue("tcp_tls", true);
  }

  function onSubmit(values: MonitorFormValues) {
    const isTCP = values.method === "TCP";
    const url = isTCP
      ? `tcp://${values.tcp_host_port.replace(/^tcp:\/\//, "")}`
      : values.url;

    mut.mutate({
      name: values.name,
      url,
      method: values.method,
      interval_minutes: Number(values.interval_minutes),
      regions: ALL_REGIONS,
      ...(isTCP && {
        tcp_tls: values.tcp_tls,
        tcp_skip_verify: values.tcp_tls ? values.tcp_skip_verify : false,
        tcp_send_string: values.tcp_send_string || null,
        tcp_expect_string: values.tcp_expect_string || null,
        tcp_timeout_secs: Number(values.tcp_timeout_secs) || 5,
      }),
      ...(isPro && {
        alert_latency_ms: values.alert_latency_ms ? Number(values.alert_latency_ms) : undefined,
        alert_cert_expiry_days: (isPro && (values.tcp_tls || !isTCP) && values.alert_cert_expiry_days)
          ? Number(values.alert_cert_expiry_days) : undefined,
        alert_email: values.alert_email || undefined,
        alert_webhook_url: values.alert_webhook_url || undefined,
        group_id: values.group_id || null,
      }),
    });
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="glass"
      style={{ padding: 24, marginBottom: 24, display: "flex", flexDirection: "column", gap: 14 }}
    >
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#EFF2F5" }}>New Monitor</h3>
      {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

      <Field label="Name" error={errors.name?.message}>
        <input {...register("name", { required: "Name is required" })} className="input-dark" placeholder={isTCP ? "Redis Production" : "My API Health Check"} />
      </Field>

      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Type">
          <select {...register("method")} className="input-dark">
            <option value="GET">HTTP GET</option>
            <option value="POST">HTTP POST</option>
            <option value="TCP">TCP Port</option>
          </select>
        </Field>
        <Field label="Interval">
          <select {...register("interval_minutes")} className="input-dark">
            {intervals.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </Field>
      </div>

      {isTCP ? (
        <TCPFields
          register={register}
          watch={watch}
          errors={errors}
          tcpTls={tcpTls}
          onApplyPreset={applyPreset}
        />
      ) : (
        <Field label="URL" error={errors.url?.message}>
          <input {...register("url", { required: !isTCP && "URL is required" })} className="input-dark" placeholder="https://api.example.com/health" />
        </Field>
      )}

      {isPro && groups && groups.length > 0 && (
        <Field label="Group (optional)">
          <select {...register("group_id")} className="input-dark">
            <option value="">No group</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </Field>
      )}

      {isPro ? (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13, color: "#98A1AB" }}>Alert settings (optional)</summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
            <Field label={isTCP ? "Alert if connection time exceeds (ms)" : "Alert if P95 latency exceeds (ms)"}>
              <input {...register("alert_latency_ms")} className="input-dark" type="number" placeholder="1000" />
            </Field>
            {(!isTCP || tcpTls) && (
              <Field label="Alert when SSL cert expires within (days)">
                <input {...register("alert_cert_expiry_days")} className="input-dark" type="number" placeholder="30" min="1" />
              </Field>
            )}
            <Field label="Alert email">
              <input {...register("alert_email")} className="input-dark" type="email" placeholder="you@example.com" />
            </Field>
            <Field label="Webhook URL">
              <input {...register("alert_webhook_url")} className="input-dark" placeholder="https://hooks.example.com/worldrtt" />
            </Field>
          </div>
        </details>
      ) : (
        <div style={{ padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 3 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#98A1AB" }}>
            Alert settings (email, webhook, latency thresholds) are available on{" "}
            <Link to="/upgrade" style={{ color: "#FF2566", textDecoration: "none" }}>Pro</Link>.
          </p>
        </div>
      )}

      <button type="submit" disabled={mut.isPending} className="btn-primary" style={{ padding: "11px 0", fontSize: 14 }}>
        {mut.isPending ? "Creating…" : "Create Monitor"}
      </button>
    </form>
  );
}

function TCPFields({
  register,
  watch,
  errors,
  tcpTls,
  onApplyPreset,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any; watch: any; errors: any;
  tcpTls: boolean;
  onApplyPreset: (p: TCPPreset) => void;
}) {
  const hostPort = watch("tcp_host_port") || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Field label="Host : Port" error={errors.tcp_host_port?.message}>
        <input
          {...register("tcp_host_port", { required: "Host:port is required (e.g. db.example.com:5432)" })}
          className="input-dark"
          placeholder="db.example.com:5432"
          spellCheck={false}
          autoComplete="off"
        />
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#98A1AB" }}>
          Enter the hostname and port of the service you want to monitor.
        </p>
      </Field>

      <div>
        <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Common services
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {TCP_PRESETS.map(preset => {
            const currentPort = hostPort.split(":")[1];
            const isActive = currentPort === String(preset.port);
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => onApplyPreset(preset)}
                title={`${preset.description} — port ${preset.port}`}
                style={{
                  padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRadius: 3,
                  border: isActive ? "1px solid rgba(255,37,102,0.6)" : "1px solid rgba(255,255,255,0.12)",
                  background: isActive ? "rgba(255,37,102,0.1)" : "rgba(255,255,255,0.04)",
                  color: isActive ? "#FF2566" : "#98A1AB",
                  transition: "all 0.15s",
                }}
              >
                {preset.label} :{preset.port}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Timeout (seconds)">
          <select {...register("tcp_timeout_secs")} className="input-dark">
            {[1, 2, 3, 5, 10, 15].map(v => (
              <option key={v} value={v}>{v}s</option>
            ))}
          </select>
        </Field>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" {...register("tcp_tls")} style={{ accentColor: "#FF2566", width: 14, height: 14 }} />
          <span style={{ fontSize: 13, color: "#EFF2F5" }}>Use TLS / SSL</span>
          <span style={{ fontSize: 12, color: "#98A1AB" }}>— encrypts the connection (needed for HTTPS, Redis TLS, Postgres TLS, etc.)</span>
        </label>

        {tcpTls && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", paddingLeft: 22 }}>
            <input type="checkbox" {...register("tcp_skip_verify")} style={{ accentColor: "#FDDD48", width: 14, height: 14 }} />
            <span style={{ fontSize: 13, color: "#EFF2F5" }}>Allow self-signed certificates</span>
            <span style={{ fontSize: 12, color: "#98A1AB" }}>— skip hostname verification</span>
          </label>
        )}
      </div>

      <details>
        <summary style={{ cursor: "pointer", fontSize: 13, color: "#98A1AB" }}>
          Banner check (optional) — verify the right service is running
        </summary>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          <p style={{ margin: 0, fontSize: 12, color: "#98A1AB", lineHeight: 1.5 }}>
            Many services send a greeting or respond to a short command. Worldrtt can send a string and check
            the response — so you know the right service is listening, not just that the port is open.
            Use <code style={{ color: "#EFF2F5" }}>\r\n</code> for carriage return / newline.
          </p>
          <Field label="Send (optional)">
            <input
              {...register("tcp_send_string")}
              className="input-dark"
              placeholder={`e.g. PING\\r\\n`}
              spellCheck={false}
            />
          </Field>
          <Field label="Expect in response (optional)">
            <input
              {...register("tcp_expect_string")}
              className="input-dark"
              placeholder={`e.g. +PONG`}
              spellCheck={false}
            />
          </Field>
        </div>
      </details>
    </div>
  );
}

interface GroupFormValues {
  name: string;
  description: string;
}

function GroupForm({ monitors, groups, onSuccess }: { monitors: Monitor[]; groups: MonitorGroup[]; onSuccess: () => void }) {
  const groupMap = new Map(groups.map(g => [g.id, g]));
  const [color, setColor] = useState("#6366F1");
  const [showPicker, setShowPicker] = useState(false);
  const [selectedMonitorIds, setSelectedMonitorIds] = useState<Set<string>>(new Set());
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { register, handleSubmit, formState: { errors } } = useForm<GroupFormValues>();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const createGroupMut = useMutation({
    mutationFn: (params: CreateMonitorGroupParams) => createMonitorGroup(params),
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to create group"),
  });

  const setGroupMut = useMutation({
    mutationFn: ({ monitorId, groupId }: { monitorId: string; groupId: string }) =>
      setMonitorGroup(monitorId, groupId),
  });

  const doSubmit = useCallback(async (values: GroupFormValues) => {
    try {
      const group = await createGroupMut.mutateAsync({
        name: values.name,
        description: values.description || null,
        color,
      });
      await Promise.all(
        Array.from(selectedMonitorIds).map(mid =>
          setGroupMut.mutateAsync({ monitorId: mid, groupId: group.id })
        )
      );
      qc.invalidateQueries({ queryKey: ["monitors"] });
      onSuccess();
    } catch {
      // error already set in mutation
    }
  }, [color, selectedMonitorIds, createGroupMut, setGroupMut, qc, onSuccess]);

  function onSubmit(values: GroupFormValues) {
    setApiError(null);

    const conflicts = monitors.filter(m => selectedMonitorIds.has(m.id) && m.group_id !== null);
    if (conflicts.length > 0) {
      const lines = conflicts.map(m => {
        const name = groupMap.get(m.group_id!)?.name ?? "another group";
        return `"${m.name}" is currently in "${name}"`;
      });
      const message = conflicts.length === 1
        ? `"${conflicts[0].name}" is currently in "${groupMap.get(conflicts[0].group_id!)?.name ?? "another group"}". Adding it here will remove it from that group.`
        : `The following monitors will be removed from their current groups:\n\n${lines.join("\n")}`;
      setConfirmModal({ title: "Move monitors to this group?", message, onConfirm: () => doSubmit(values) });
      return;
    }

    doSubmit(values);
  }

  function toggleMonitor(id: string) {
    setSelectedMonitorIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const isPending = createGroupMut.isPending || setGroupMut.isPending;

  return (
    <>
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Move to Group"
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal(null)}
        />
      )}
      <form
      onSubmit={handleSubmit(onSubmit)}
      className="glass"
      style={{ padding: 24, marginBottom: 24, display: "flex", flexDirection: "column", gap: 16 }}
    >
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#EFF2F5" }}>New Group</h3>
      {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <Field label="Name" error={errors.name?.message} style={{ flex: 1 }}>
          <input {...register("name", { required: "Name is required" })} className="input-dark" placeholder="Production Services" />
        </Field>
        <Field label="Color">
          <div ref={pickerRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setShowPicker(v => !v)}
              style={{
                width: 38, height: 38, borderRadius: 3, border: "2px solid rgba(255,255,255,0.2)",
                background: color, cursor: "pointer", display: "block",
              }}
              title="Pick a color"
            />
            {showPicker && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 8px)", left: 0, zIndex: 100,
                background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 6, padding: 10,
                display: "grid", gridTemplateColumns: "repeat(6, 24px)", gap: 5,
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              }}>
                {GROUP_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setColor(c); setShowPicker(false); }}
                    style={{
                      width: 24, height: 24, borderRadius: 3, background: c, cursor: "pointer",
                      border: c === color ? "2px solid #fff" : "2px solid transparent",
                      padding: 0,
                    }}
                    title={c}
                  />
                ))}
              </div>
            )}
          </div>
        </Field>
      </div>

      <Field label="Description (optional)">
        <textarea
          {...register("description")}
          className="input-dark"
          placeholder="Monitors for production API endpoints"
          rows={2}
          style={{ resize: "vertical", minHeight: 60 }}
        />
      </Field>

      {monitors.length > 0 && (
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#98A1AB", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Add monitors (optional)
          </label>
          <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {monitors.map(m => (
              <label
                key={m.id}
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 10px", borderRadius: 3, background: selectedMonitorIds.has(m.id) ? "rgba(255,255,255,0.06)" : "transparent" }}
              >
                <input
                  type="checkbox"
                  checked={selectedMonitorIds.has(m.id)}
                  onChange={() => toggleMonitor(m.id)}
                  style={{ accentColor: color, width: 14, height: 14, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: "#EFF2F5", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
                <span style={{ fontSize: 12, color: "#98A1AB", flexShrink: 0, marginLeft: "auto" }}>{m.url.replace(/^https?:\/\//, "")}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <button type="submit" disabled={isPending} className="btn-primary" style={{ padding: "11px 0", fontSize: 14 }}>
        {isPending ? "Creating…" : "Create Group"}
      </button>
    </form>
    </>
  );
}

function Field({ label, children, error, style }: { label: string; children: React.ReactNode; error?: string; style?: React.CSSProperties }) {
  return (
    <div style={{ flex: 1, ...style }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#98A1AB", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </label>
      {children}
      {error && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#FF5C5C" }}>{error}</p>}
    </div>
  );
}

// Re-export MonitorForm for use on the group detail page
export { MonitorForm };
